// Lead Sources Master — AdminCatalogue sub-tab.
// List + add/edit + activate/deactivate + delete-if-unused + automated-test-row cleanup.

import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { API } from "../../App";
import { toast } from "sonner";
import {
  Plus, PencilSimple, Trash, ToggleLeft, ToggleRight, MagnifyingGlass,
  X, Check, Warning, Broom, Tag, Lock,
} from "@phosphor-icons/react";

const inputCls = "rounded-none w-full bg-white border border-slate-300 focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] p-2.5 text-sm outline-none";

// 19 defaults — kept in sync with backend `_DEFAULT_LEAD_SOURCES`.
const DEFAULT_SLUGS = new Set([
  "manual_entry", "digital_marketing", "website", "product_enquiry",
  "social_capture", "campaign", "referral", "import",
  "whatsapp", "phone_call", "existing_customer", "dealer_reference",
  "bde_self_generated", "google_ads", "facebook_instagram",
  "indiamart", "justdial", "tradeindia", "other",
]);

const LeadSourcesTab = ({ user }) => {
  const role = (user?.role || "").toLowerCase();
  const canManage = ["admin", "md"].includes(role);
  const isAdmin = role === "admin";

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showInactive, setShowInactive] = useState(true);
  const [editor, setEditor] = useState(null); // { mode, data }
  const [cleaning, setCleaning] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/leads/sources`, {
        params: { include_inactive: showInactive, with_usage: true },
      });
      setRows(r.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load sources");
    } finally {
      setLoading(false);
    }
  }, [showInactive]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      (r.name || "").toLowerCase().includes(q) ||
      (r.slug || "").toLowerCase().includes(q) ||
      (r.description || "").toLowerCase().includes(q)
    );
  }, [rows, search]);

  const setActive = async (row, next) => {
    if (!canManage) return;
    try {
      const path = next ? "activate" : "deactivate";
      await axios.patch(`${API}/leads/sources/${row.slug}/${path}`, { requester_role: user.role });
      toast.success(`${row.name} ${next ? "activated" : "deactivated"}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const remove = async (row) => {
    if (!canManage) return;
    if (!window.confirm(`Delete source "${row.name}"? This is only possible if no leads use it.`)) return;
    try {
      await axios.delete(`${API}/leads/sources/${row.slug}`, { params: { requester_role: user.role } });
      toast.success(`Deleted "${row.name}"`);
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail || "Delete failed";
      toast.error(detail);
    }
  };

  const cleanupTestRows = async () => {
    if (!isAdmin) return;
    if (!window.confirm("Clean automated-test source rows? Only AgentTest_/BdeBlocked_/DeactSrc_/DeleteSrc_/EditSrc_ prefixed rows will be touched. Default master sources are protected.")) return;
    setCleaning(true);
    try {
      const r = await axios.post(`${API}/leads/sources/_cleanup_test_rows`, { requester_role: user.role });
      const d = r.data || {};
      toast.success(`Cleanup: ${d.deleted?.length || 0} deleted, ${d.deactivated?.length || 0} deactivated, ${d.skipped?.length || 0} skipped (matched ${d.total_matched || 0})`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cleanup failed");
    } finally {
      setCleaning(false);
    }
  };

  return (
    <div data-testid="adm-lead-sources">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search source name, slug, description…"
            className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded text-sm outline-none focus:border-[#002FA7] bg-white"
            data-testid="ls-search"
          />
        </div>
        <label className="flex items-center gap-2 text-xs text-slate-700">
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
            data-testid="ls-show-inactive"
            className="w-4 h-4 accent-[#002FA7]"
          />
          Show inactive + legacy
        </label>
        {canManage && (
          <button
            onClick={() => setEditor({ mode: "create", data: { name: "", description: "", is_active: true } })}
            className="bg-[#002FA7] text-white hover:bg-[#00227A] px-4 py-2 text-sm font-bold flex items-center gap-2"
            data-testid="ls-new"
          >
            <Plus size={14} weight="bold" /> Add Source
          </button>
        )}
        {isAdmin && (
          <button
            onClick={cleanupTestRows}
            disabled={cleaning}
            className="border border-amber-500 text-amber-700 hover:bg-amber-50 px-3 py-2 text-xs font-bold flex items-center gap-2 disabled:opacity-50"
            title="Hard-delete unused test rows, deactivate used ones. Defaults are protected."
            data-testid="ls-cleanup-tests"
          >
            <Broom size={14} weight="bold" /> {cleaning ? "Cleaning…" : "Clean Test Rows"}
          </button>
        )}
        {!canManage && (
          <span className="inline-flex items-center gap-1 text-[11px] text-slate-500 bg-slate-100 px-2 py-1">
            <Lock size={11} /> View-only — admin/MD can manage
          </span>
        )}
      </div>

      {loading ? (
        <div className="py-10 flex justify-center"><div className="loading-spinner" /></div>
      ) : (
        <div className="bg-white border border-slate-200 rounded overflow-x-auto">
          <table className="w-full text-sm" data-testid="ls-table">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 font-bold">
                <th className="p-3">Source Name</th>
                <th className="p-3">Slug</th>
                <th className="p-3">Description</th>
                <th className="p-3 text-center">Status</th>
                <th className="p-3 text-right">Usage</th>
                <th className="p-3">Kind</th>
                <th className="p-3 text-right"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan="7" className="p-10 text-center text-slate-500">No sources match this search.</td></tr>
              ) : filtered.map((r) => {
                const isLegacy = !!r.is_legacy;
                const isDefault = DEFAULT_SLUGS.has(r.slug);
                const usage = r.usage_count ?? 0;
                const deleteBlocked = isDefault || usage > 0 || isLegacy;
                const deleteTooltip = isLegacy
                  ? "Legacy value (auto-detected) — has no master row to delete"
                  : isDefault
                  ? "Default master source — protected"
                  : usage > 0
                  ? `In use by ${usage} lead(s) — deactivate instead`
                  : "Delete";
                return (
                  <tr key={r.slug + (isLegacy ? "_l" : "")} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`ls-row-${r.slug}`}>
                    <td className="p-3 font-semibold">{r.name}</td>
                    <td className="p-3 text-xs font-mono text-slate-600">{r.slug}</td>
                    <td className="p-3 text-xs text-slate-600 max-w-[280px] truncate" title={r.description}>{r.description || "—"}</td>
                    <td className="p-3 text-center">
                      {isLegacy ? (
                        <span className="text-[10px] uppercase tracking-wider bg-slate-200 text-slate-700 px-2 py-0.5 font-bold">N/A</span>
                      ) : (
                        <button
                          onClick={() => setActive(r, !r.is_active)}
                          disabled={!canManage}
                          className="inline-flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                          title={r.is_active ? "Deactivate" : "Activate"}
                          data-testid={`ls-toggle-${r.slug}`}
                        >
                          {r.is_active
                            ? <ToggleRight size={24} weight="fill" className="text-emerald-500" />
                            : <ToggleLeft size={24} weight="fill" className="text-slate-400" />}
                        </button>
                      )}
                    </td>
                    <td className="p-3 text-right tabular-nums" data-testid={`ls-usage-${r.slug}`}>{usage}</td>
                    <td className="p-3">
                      {isLegacy ? (
                        <span className="text-[10px] uppercase tracking-wider bg-amber-100 text-amber-800 px-2 py-0.5 font-bold">Legacy</span>
                      ) : isDefault ? (
                        <span className="text-[10px] uppercase tracking-wider bg-blue-100 text-blue-800 px-2 py-0.5 font-bold">Default</span>
                      ) : (
                        <span className="text-[10px] uppercase tracking-wider bg-slate-100 text-slate-700 px-2 py-0.5 font-bold">Master</span>
                      )}
                    </td>
                    <td className="p-3 text-right whitespace-nowrap">
                      {!isLegacy && canManage && (
                        <button
                          onClick={() => setEditor({ mode: "edit", data: r })}
                          className="p-1.5 hover:bg-slate-100 text-[#002FA7]"
                          title="Edit"
                          data-testid={`ls-edit-${r.slug}`}
                        ><PencilSimple size={14} /></button>
                      )}
                      {!isLegacy && canManage && (
                        <button
                          onClick={() => !deleteBlocked && remove(r)}
                          disabled={deleteBlocked}
                          className={`p-1.5 ${deleteBlocked ? "text-slate-300 cursor-not-allowed" : "hover:bg-red-50 text-red-600"}`}
                          title={deleteTooltip}
                          data-testid={`ls-delete-${r.slug}`}
                        ><Trash size={14} /></button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {editor && (
        <LeadSourceEditor
          editor={editor}
          user={user}
          onClose={() => setEditor(null)}
          onSaved={() => { setEditor(null); load(); }}
        />
      )}
    </div>
  );
};

const LeadSourceEditor = ({ editor, user, onClose, onSaved }) => {
  const isEdit = editor.mode === "edit";
  const [name, setName] = useState(editor.data?.name || "");
  const [description, setDescription] = useState(editor.data?.description || "");
  const [isActive, setIsActive] = useState(editor.data?.is_active !== false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const save = async () => {
    setErr("");
    if (!name.trim()) { setErr("Source name is required"); return; }
    setSaving(true);
    try {
      if (isEdit) {
        await axios.put(`${API}/leads/sources/${editor.data.slug}`, {
          name, description, is_active: isActive, requester_role: user.role,
        });
        toast.success(`Updated "${name}"`);
      } else {
        await axios.post(`${API}/leads/sources`, {
          name, description, is_active: isActive,
          requester_user_id: user.user_id, requester_name: user.name, requester_role: user.role,
        });
        toast.success(`Created "${name}"`);
      }
      onSaved();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setErr(typeof detail === "string" ? detail : "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-start justify-center p-4 overflow-y-auto"
      onClick={onClose}
    >
      <div className="bg-white shadow-2xl max-w-lg w-full my-12" onClick={(e) => e.stopPropagation()} data-testid="ls-editor">
        <div className="border-b border-slate-200 px-5 py-3 flex justify-between items-center">
          <h2 className="text-lg font-bold">{isEdit ? "Edit" : "New"} Lead Source</h2>
          <button onClick={onClose} className="p-1 hover:bg-slate-100"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-3">
          {err && (
            <div className="flex items-start gap-2 border border-rose-300 bg-rose-50 text-rose-800 text-sm p-3">
              <Warning size={14} weight="duotone" className="shrink-0 mt-0.5" />
              <div>{err}</div>
            </div>
          )}
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-slate-600">Source Name *</label>
            <input
              data-testid="ls-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className={inputCls + " mt-1"}
              autoFocus
            />
            {isEdit && (
              <div className="text-[10px] text-slate-500 mt-1">Slug (auto, immutable): <span className="font-mono">{editor.data.slug}</span></div>
            )}
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-slate-600">Description</label>
            <textarea
              data-testid="ls-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              className={inputCls + " mt-1"}
            />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input
              data-testid="ls-active"
              type="checkbox"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
              className="w-4 h-4 accent-[#002FA7]"
            />
            Active (visible in form dropdowns)
          </label>
        </div>
        <div className="border-t border-slate-200 px-5 py-3 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-xs uppercase tracking-wider font-bold border border-slate-300 hover:bg-slate-50">Cancel</button>
          <button
            onClick={save}
            disabled={saving}
            data-testid="ls-save"
            className="px-4 py-2 text-xs uppercase tracking-wider font-bold bg-[#002FA7] text-white hover:bg-[#00227A] disabled:bg-slate-300 flex items-center gap-2"
          >
            <Check size={12} weight="bold" /> {saving ? "Saving…" : (isEdit ? "Update" : "Create")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default LeadSourcesTab;
